import React, { Component } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { AlertDiamondIcon, Refresh01Icon } from '@hugeicons/core-free-icons'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

/**
 * Global Error Boundary for catching and displaying unhandled React errors
 *
 * Features:
 * - Catches JavaScript errors anywhere in the child component tree
 * - Logs error details for debugging
 * - Shows user-friendly error UI with retry option
 * - Preserves app functionality outside the error boundary
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    })

    // Here you could send the error to an error reporting service like Sentry
    // Example:
    // import * as Sentry from '@sentry/react'
    // Sentry.captureException(error, { contexts: { react: errorInfo } })
  }

  handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    })
  }

  handleReload = () => {
    // Full page reload as last resort
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI or use the provided fallback
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <HugeiconsIcon
                  icon={AlertDiamondIcon}
                  className="h-6 w-6 text-destructive"
                />
              </div>
              <CardTitle className="text-destructive">
                Something went wrong
              </CardTitle>
              <CardDescription>
                An unexpected error occurred. This has been logged and we're
                working on it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Error Details (Development)
                  </summary>
                  <div className="mt-2 p-2 bg-muted rounded text-xs font-mono overflow-auto max-h-32">
                    <div className="text-destructive font-semibold">
                      {this.state.error.name}
                    </div>
                    <div className="mb-2">{this.state.error.message}</div>
                    {this.state.error.stack && (
                      <pre className="whitespace-pre-wrap text-muted-foreground">
                        {this.state.error.stack}
                      </pre>
                    )}
                  </div>
                </details>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={this.handleRetry}
                  variant="outline"
                  className="flex-1"
                >
                  <HugeiconsIcon
                    icon={Refresh01Icon}
                    className="mr-2 h-4 w-4"
                  />
                  Try Again
                </Button>
                <Button
                  onClick={this.handleReload}
                  variant="default"
                  className="flex-1"
                >
                  Reload Page
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                If this persists, please contact support with the error details.
              </p>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook-based error boundary for functional components
 * Use this to catch errors in specific sections of your app
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null)

  const resetError = React.useCallback(() => {
    setError(null)
  }, [])

  const captureError = React.useCallback((err: Error) => {
    setError(err)
  }, [])

  // Throw error to trigger nearest error boundary
  if (error) {
    throw error
  }

  return { captureError, resetError }
}
